import axios from "axios";

// How long (ms) after a user interaction to treat any API call as "user-triggered".
// During this window, ignoreActivityLog=true will NOT be appended.
const USER_INTERACTION_GRACE_MS = 1000;

const PARAM_KEY = "ignoreActivityLog";
const PARAM_VALUE = "true";

// Timestamp of the most recent user interaction (click, keydown, touchstart, submit).
// Starts at -Infinity so the very first auto calls on page load are always treated as auto.
let lastUserInteractionTime = -Infinity;

// When > Date.now(), ALL API calls get ignoreActivityLog=true regardless of user interaction.
// Used by landing-page components to mark auto-fired calls on page load / tab switch.
let forceIgnoreUntil = -Infinity;

// When > Date.now(), API calls are treated as explicitly user-triggered (NOT ignored).
// Takes priority over forceIgnoreUntil. Used when user clicks date filter buttons.
let dateInteractionUntil = -Infinity;

function recordUserInteraction() {
	lastUserInteractionTime = Date.now();
}

function isUserTriggered() {
	return Date.now() - lastUserInteractionTime < USER_INTERACTION_GRACE_MS;
}

function isAbsoluteUrl(url) {
	return /^https?:\/\//i.test(url);
}

function toAbsoluteUrl(url, baseURL) {
	try {
		if (isAbsoluteUrl(url)) {
			return new URL(url);
		}
		if (baseURL) {
			return new URL(url, baseURL);
		}
		return new URL(url, window.location.origin);
	} catch (error) {
		return null;
	}
}

function isLoginEndpoint(pathname) {
	const normalizedPath = (pathname || "").toLowerCase();
	return (
		normalizedPath.includes("/client/login") ||
		normalizedPath.includes("/oauth/token")
	);
}

function shouldDecorateRequest(urlObj) {
	if (!urlObj) {
		return false;
	}
	if (isLoginEndpoint(urlObj.pathname)) {
		return false;
	}

	const now = Date.now();

	// Explicit date-interaction override — highest priority.
	// When a user clicks a date filter button, these calls should be tracked.
	if (dateInteractionUntil > now) {
		return false;
	}

	// Force-ignore override — page load / tab switch marked these calls.
	if (forceIgnoreUntil > now) {
		return true;
	}

	// Fallback: Only decorate if the call was NOT triggered by a recent user interaction.
	if (isUserTriggered()) {
		return false;
	}
	return true;
}

function appendParamToAxiosConfig(config) {
	const hasParamInConfig =
		config.params &&
		Object.prototype.hasOwnProperty.call(config.params, PARAM_KEY);

	const absoluteUrl = toAbsoluteUrl(config.url, config.baseURL);
	const hasParamInUrl = absoluteUrl
		? absoluteUrl.searchParams.has(PARAM_KEY)
		: false;

	if (!hasParamInConfig && !hasParamInUrl) {
		config.params = {
			...(config.params || {}),
			[PARAM_KEY]: PARAM_VALUE,
		};
	}

	return config;
}

function appendParamToFetchUrl(inputUrl) {
	try {
		const urlObj = isAbsoluteUrl(inputUrl)
			? new URL(inputUrl)
			: new URL(inputUrl, window.location.origin);

		if (!urlObj.searchParams.has(PARAM_KEY)) {
			urlObj.searchParams.append(PARAM_KEY, PARAM_VALUE);
		}
		return urlObj.toString();
	} catch {
		return inputUrl;
	}
}

function setupUserInteractionListeners() {
	if (window.__activityLogInteractionListenersInstalled) {
		return;
	}

	const INTERACTION_EVENTS = ["click", "keydown", "touchstart", "submit"];
	INTERACTION_EVENTS.forEach((eventName) => {
		document.addEventListener(eventName, recordUserInteraction, {
			capture: true,
			passive: true,
		});
	});

	window.__activityLogInteractionListenersInstalled = true;
}

function setupAxiosInterceptor() {
	if (window.__activityLogAxiosInterceptorInstalled) {
		return;
	}

	axios.interceptors.request.use((config) => {
		const targetUrl = config?.url;
		if (!targetUrl) {
			return config;
		}

		// Explicit Opt-Out of Ignore logic for specific API calls
		// If custom config property trackActivity is true, skip adding ignoreActivityLog.
		if (config.trackActivity) {
			return config;
		}

		const absoluteUrl = toAbsoluteUrl(targetUrl, config.baseURL);
		if (!shouldDecorateRequest(absoluteUrl)) {
			return config;
		}

		return appendParamToAxiosConfig(config);
	});

	window.__activityLogAxiosInterceptorInstalled = true;
}

function setupFetchDecorator() {
	if (
		window.__activityLogFetchDecoratorInstalled ||
		typeof window.fetch !== "function"
	) {
		return;
	}

	const originalFetch = window.fetch.bind(window);

	window.fetch = (input, init) => {
		const sourceUrl =
			input instanceof Request ? input.url : String(input);
		const urlObj = toAbsoluteUrl(sourceUrl);

		if (!shouldDecorateRequest(urlObj)) {
			return originalFetch(input, init);
		}

		const decoratedUrl = appendParamToFetchUrl(sourceUrl);

		if (input instanceof Request) {
			return originalFetch(new Request(decoratedUrl, new Request(input, init)));
		}

		return originalFetch(decoratedUrl, init);
	};

	window.__activityLogFetchDecoratorInstalled = true;
}

/**
 * Call from landing-page components on page-load / tab-switch so that
 * all auto-fired API calls within the next `durationMs` get
 * ignoreActivityLog=true, overriding the user-interaction timer.
 *
 * @param {number} [durationMs=5000] - how long to force-ignore.
 */
export function markNextCallsAsIgnored(durationMs = 5000) {
	forceIgnoreUntil = Date.now() + durationMs;
}

/**
 * Call when the user explicitly clicks a date-filter button
 * (Current Month / Previous Month / Custom Date) so that the
 * subsequent API calls are tracked as real user activity.
 * Takes priority over markNextCallsAsIgnored.
 *
 * @param {number} [durationMs=3000] - how long to treat calls as user-triggered.
 */
export function markDateInteraction(durationMs = 3000) {
	dateInteractionUntil = Date.now() + durationMs;
}

export function initializeActivityLogRequestTracking() {
	if (typeof window === "undefined") {
		return;
	}

	setupUserInteractionListeners();
	setupAxiosInterceptor();
	setupFetchDecorator();
}

